import React, { useEffect, useId, useMemo, useState } from 'react';

import {
  DefectCatalogItem,
} from 'domain/surveyDataManager/contracts';
import {
  useActiveLayerVisualizationSettings,
  usePatchActiveLayerVisualizationSettings,
} from 'domain/surveyDataManager/hooks';
import { buildBandLegendItems } from 'domain/surveyDataManager/map/buildRulesFromColorScheme';
import {
  ActiveLayerVisualizationSettings,
  findCatalogMeasurement,
  isValidHexColor,
  normalizeThresholds,
  resolveInitialVisualizationSettings,
} from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  resolveDefectBandMeaningDescription,
  resolveDefectBandMeaningLabels,
} from 'domain/surveyDataManager/visualization/defectBandMeanings';
import {
  filterLegendItemsToWorstCondition,
  isWorstConditionRuleset,
  resolveWorstConditionThreshold,
} from 'domain/surveyDataManager/visualization/worstConditionOnly';
import { ActiveDefectLayer } from 'state/surveyDataManager';

import '../AddLayerWizard/AddLayerWizard.scss';

export interface ActiveLayerAdvancedSettingsProps {
  readonly layer: ActiveDefectLayer;
  readonly defects: readonly DefectCatalogItem[];
  readonly isOpen: boolean;
  onToggle: () => void;
  onSave: (settings: ActiveLayerVisualizationSettings) => void;
}

function ActiveLayerAdvancedSettings({
  layer,
  defects,
  isOpen,
  onToggle,
  onSave,
}: ActiveLayerAdvancedSettingsProps): JSX.Element {
  const conditionThresholdId = useId();

  const settingsQuery = useActiveLayerVisualizationSettings(layer.instanceId, isOpen);
  const patchMutation = usePatchActiveLayerVisualizationSettings();

  const catalogDefect = useMemo(
    () => defects.find((defect) => defect.slug === layer.defectSlug) ?? null,
    [defects, layer.defectSlug],
  );

  const catalogRule = useMemo(
    () => findCatalogMeasurement(defects, layer.defectSlug, layer.measurementSlug),
    [defects, layer.defectSlug, layer.measurementSlug],
  );

  const meaningContext = useMemo(
    () => ({
      surveyType: catalogDefect?.surveyType,
      measurementSlug: String(layer.measurementSlug),
      parameter: catalogRule?.parameter,
      code: catalogRule?.code,
      measurementName: catalogRule?.name,
      defectType: catalogDefect?.type,
    }),
    [
      catalogDefect?.surveyType,
      catalogDefect?.type,
      catalogRule?.code,
      catalogRule?.name,
      catalogRule?.parameter,
      layer.measurementSlug,
    ],
  );

  const resolvedSettings = useMemo(
    () =>
      resolveInitialVisualizationSettings({
        catalogRule,
        apiSettings: settingsQuery.data?.settings ?? null,
        savedSettings: layer.visualizationSettings,
        measurementSlug: layer.measurementSlug,
      }),
    [
      catalogRule,
      layer.measurementSlug,
      layer.visualizationSettings,
      settingsQuery.data?.settings,
    ],
  );

  const [draft, setDraft] = useState<ActiveLayerVisualizationSettings | null>(resolvedSettings);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || settingsQuery.isLoading) {
      return;
    }
    setDraft(resolvedSettings);
    setSaveError(null);
  }, [isOpen, resolvedSettings, settingsQuery.isLoading, layer.id]);

  const gradientStyle =
    draft && isValidHexColor(draft.colorLow) && isValidHexColor(draft.colorHigh)
      ? {
          background: `linear-gradient(to right, ${draft.colorLow}, ${draft.colorHigh})`,
        }
      : undefined;

  const isWorstCondition = isWorstConditionRuleset(String(layer.rulesetSlug));

  const bandLegendItems = (() => {
    if (
      !draft ||
      !isValidHexColor(draft.colorLow) ||
      !isValidHexColor(draft.colorHigh)
    ) {
      return [];
    }
    const items = buildBandLegendItems(
      { min: draft.colorLow, max: draft.colorHigh },
      resolveDefectBandMeaningLabels(meaningContext),
    );
    return isWorstCondition ? filterLegendItemsToWorstCondition(items) : items;
  })();

  const conditionThreshold = draft
    ? resolveWorstConditionThreshold(draft.thresholds)
    : null;

  const presetDescription =
    draft?.ruleDescription.trim() ||
    catalogRule?.description.trim() ||
    (isWorstCondition
      ? 'Displays only records exceeding a defined condition threshold.'
      : resolveDefectBandMeaningDescription(meaningContext));

  const handleSave = async (): Promise<void> => {
    if (!draft) {
      setSaveError('No visualisation preset is available for this layer.');
      return;
    }

    if (!isWorstCondition) {
      setSaveError('This visualisation preset is read-only.');
      return;
    }

    if (!isValidHexColor(draft.colorLow) || !isValidHexColor(draft.colorHigh)) {
      setSaveError('Colour values must be valid hex codes (e.g. #10b981).');
      return;
    }

    const payload: ActiveLayerVisualizationSettings = {
      ...draft,
      measurementSlug: layer.measurementSlug,
      ruleDescription: presetDescription,
      thresholds: normalizeThresholds(draft.thresholds),
      savedAt: new Date().toISOString(),
    };

    try {
      const response = await patchMutation.mutateAsync({
        instanceId: layer.instanceId,
        body: { settings: payload },
      });
      if (response.settings.measurementSlug === layer.measurementSlug) {
        onSave(response.settings);
      }
      setSaveError(null);
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Could not save visualisation settings.';
      setSaveError(message);
      throw error;
    }
  };

  const loadErrorMessage =
    settingsQuery.isError && settingsQuery.error
      ? settingsQuery.error.message
      : null;

  return (
    <div className="sdm-active-layers__advanced">
      <button
        type="button"
        className="sdm-active-layers__advanced-toggle"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? 'Hide visualisation settings' : 'Visualisation settings'}
      </button>

      {isOpen ? (
        <div
          className="sdm-active-layers__advanced-panel"
          aria-label={`Visualisation settings for ${layer.displayName}`}
        >
          <h4 className="sdm-active-layers__settings-title">Visualisation preset</h4>
          <p className="sdm-active-layers__advanced-parameter">
            Parameter: {catalogRule?.name ?? layer.measurementSlug}
          </p>

          {settingsQuery.isLoading ? (
            <p className="sdm-active-layers__advanced-empty" role="status">
              Loading saved visualisation settings…
            </p>
          ) : loadErrorMessage ? (
            <p className="sdm-active-layers__save-error" role="alert">
              {loadErrorMessage}
            </p>
          ) : !draft ? (
            <p className="sdm-active-layers__advanced-empty" role="note">
              A default visualisation preset is not available for this parameter yet.
            </p>
          ) : (
            <>
              <div className="sdm-active-layers__readonly-field">
                <span className="sdm-active-layers__field-label">Preset name</span>
                <p className="sdm-active-layers__readonly-value">{draft.ruleName}</p>
              </div>

              <div className="sdm-active-layers__readonly-field">
                <span className="sdm-active-layers__field-label">Description</span>
                <p className="sdm-active-layers__readonly-value">
                  {presetDescription || 'No description for this visualisation preset.'}
                </p>
              </div>

              {isWorstCondition ? (
                <label className="sdm-active-layers__field" htmlFor={conditionThresholdId}>
                  <span className="sdm-active-layers__field-label">Condition threshold</span>
                  <input
                    id={conditionThresholdId}
                    type="number"
                    className="sdm-active-layers__text-input"
                    value={conditionThreshold ?? ''}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value);
                      if (!Number.isFinite(nextValue)) {
                        return;
                      }
                      setDraft((current) => {
                        if (!current) {
                          return current;
                        }
                        const thresholds = [...normalizeThresholds(current.thresholds)];
                        thresholds[thresholds.length - 1] = nextValue;
                        return { ...current, thresholds };
                      });
                    }}
                  />
                  <span className="sdm-active-layers__field-hint">
                    Only records at or above this value (or high / very-high bands) are shown.
                  </span>
                </label>
              ) : null}

              <div className="sdm-active-layers__color-scheme">
                <span className="sdm-active-layers__field-label">Colour scheme</span>
                <p className="sdm-active-layers__field-hint">
                  Preset colours are defined by XAIS-PTS / client standards and cannot be changed here.
                </p>
                <div className="sdm-active-layers__color-row">
                  <div className="sdm-active-layers__readonly-field">
                    <span className="sdm-active-layers__field-label">Low severity</span>
                    <p className="sdm-active-layers__readonly-value">{draft.colorLow}</p>
                  </div>
                  <div className="sdm-active-layers__readonly-field">
                    <span className="sdm-active-layers__field-label">High severity</span>
                    <p className="sdm-active-layers__readonly-value">{draft.colorHigh}</p>
                  </div>
                  {gradientStyle ? (
                    <div
                      className="sdm-active-layers__gradient-preview"
                      style={gradientStyle}
                      aria-hidden
                    />
                  ) : null}
                </div>
              </div>

              {bandLegendItems.length > 0 ? (
                <div
                  className="sdm-active-layers__legend sdm-active-layers__legend--embedded"
                  aria-label={`Colour band meanings for ${draft.ruleName}`}
                >
                  <span className="sdm-active-layers__field-label">Colour band meanings</span>
                  <ul className="sdm-active-layers__legend-list">
                    {bandLegendItems.map((item) => (
                      <li key={item.slug} className="sdm-active-layers__legend-item">
                        <span
                          className="sdm-active-layers__legend-swatch"
                          style={{ backgroundColor: item.color }}
                          aria-hidden
                        />
                        <span className="sdm-active-layers__legend-label">{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {saveError ? (
                <p className="sdm-active-layers__save-error" role="alert">
                  {saveError}
                </p>
              ) : null}

              {isWorstCondition ? (
                <button
                  type="button"
                  className="sdm-active-layers__save-button"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={patchMutation.isPending}
                >
                  {patchMutation.isPending ? 'Saving…' : 'Save visualisation settings'}
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default ActiveLayerAdvancedSettings;
